import { useMemo, useState } from 'react';
import { addItemsToCart } from '@entities/cart/model';
import { useAppDispatch } from '@app/store/store';
import { Order, OrderItem } from '../../../types/interfaces';
import { MyModal } from '@shared/ui/my-modal';
import { Button } from '@shared/ui/button';

interface RepeatOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: Order;
}


interface GroupedItem {
  main: OrderItem;
  verification?: OrderItem;
  passport?: OrderItem;
}

export function RepeatOrderModal({ isOpen, onClose, order }: RepeatOrderModalProps) {
  const dispatch = useAppDispatch();

  const groups = useMemo<GroupedItem[]>(() => {
    const items = order.items ?? [];
    const byItemId = new Map<number, OrderItem>();
    items.forEach((it) => byItemId.set(it.item, it));

    // id позиций, которые являются допом какого-то основного товара.
    const addonItemIds = new Set<number>();
    items.forEach((it) => {
      if (it.verification_item) addonItemIds.add(it.verification_item);
      if (it.passport_item) addonItemIds.add(it.passport_item);
    });

    return items
      .filter((it) => !addonItemIds.has(it.item))
      .map((main) => ({
        main,
        verification: main.verification_item
          ? byItemId.get(main.verification_item)
          : undefined,
        passport: main.passport_item
          ? byItemId.get(main.passport_item)
          : undefined,
      }));
  }, [order.items]);

  const [productQuantities, setProductQuantities] = useState<Record<string, number>>(
    () =>
      groups.reduce((acc: Record<string, number>, g) => {
        acc[g.main.item] = g.main.quantity;
        return acc;
      }, {})
  );

  const handleQuantityChange = (item: number, value: number) => {
    setProductQuantities((prev) => ({ ...prev, [item]: value }));
  };

  const handleAddToCart = () => {
    const payload = groups.map((g) => ({
      id: g.main.item,
      quantity: productQuantities[g.main.item],
      verification_item: g.verification ? g.main.verification_item ?? null : null,
      verification_quantity: g.verification ? g.verification.quantity : 0,
      passport_item: g.passport ? g.main.passport_item ?? null : null,
      passport_quantity: g.passport ? g.passport.quantity : 0,
    }));
    dispatch(addItemsToCart({ items: payload }));
    onClose();
  };

  return (
    <MyModal isOpen={isOpen} onClose={onClose} title="Повтор заказа" size="lg">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Наименование товара
            </th>
            <th className="text-left py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Количество
            </th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => (
            <tr
              key={g.main.item}
              className="border-b border-gray-100 last:border-b-0"
            >
              <td className={`py-3 ${g.main.cancel ? 'text-red-400' : ''}`}>
                {g.main.item_name}
                {(g.verification || g.passport) && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {g.passport && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-600">
                        Паспорт ×{g.passport.quantity}
                      </span>
                    )}
                    {g.verification && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-600">
                        Поверка ×{g.verification.quantity}
                      </span>
                    )}
                  </div>
                )}
              </td>
              <td className="py-3">
                <input
                  type="number"
                  className="border border-gray-300 rounded-md px-2 py-1 w-20 text-sm focus:outline-none focus:border-[#007aff]"
                  value={productQuantities[g.main.item]}
                  onChange={(e) =>
                    handleQuantityChange(g.main.item, +e.target.value)
                  }
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-6 flex justify-end gap-2">
        <Button isWhite onClick={onClose}>
          Отмена
        </Button>
        <Button onClick={handleAddToCart}>В корзину</Button>
      </div>
    </MyModal>
  );
}
